import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Trophy } from 'lucide-react';
import { PromptDetailModal } from '@/components/shared/PromptDetailModal';
import { FavoriteButton } from '@/components/shared/FavoriteButton';
import { getScoreColor } from '@/lib/scoreUtils';

interface TopPrompt {
  score_id: string;
  score_total: number;
  detected_type: string;
  best_prompt: string | null;
  user_id: string;
  user_name: string;
  created_at: string;
  evaluation_markdown: string | null;
  tags?: string[] | null;
}

interface PromptDetailData {
  score_id: string;
  overall_score: number;
  detected_type: string | null;
  best_prompt_extracted: string | null;
  dimension_scores_json: Record<string, unknown> | null;
  evaluation_markdown: string | null;
  chatcraft_subscores_json: Record<string, number> | null;
  original_content: string | null;
  submission_type: string | null;
  points_total: number | null;
  points_base?: number | null;
  points_rubric?: number | null;
  points_bonus?: number | null;
  user_name?: string | null;
  created_at: string;
}

interface TopPromptsRankedProps {
  orgId: string;
  maxHeight?: string;
  compactHeader?: boolean;
  limit?: number;
  title?: string;
  description?: string;
  promptTypeFilter?: string | null;
  tagFilter?: string | null;
  showLibraryCta?: boolean;
}

interface PromptRpcRow {
  score_id: string;
  score_total: number;
  detected_type?: string | null;
  best_prompt: string | null;
  user_id: string;
  user_name: string;
  created_at: string;
  evaluation_markdown: string | null;
  tags?: string[] | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Planning': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Strategy': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Research': 'bg-green-500/20 text-green-300 border-green-500/30',
  'Drafting': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  'Analysis': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'Creative': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  'Unknown': 'bg-muted text-muted-foreground border-border',
};

function getPromptTitle(prompt: TopPrompt): string {
  if (prompt.evaluation_markdown) {
    try {
      const parsed = JSON.parse(prompt.evaluation_markdown);
      if (parsed.prompt_title) return parsed.prompt_title;
    } catch {
      // Some older rows store plain markdown instead of JSON metadata.
    }
  }
  // Fall back to first line of best_prompt
  if (prompt.best_prompt) {
    const firstLine = prompt.best_prompt.split('\n')[0].trim();
    if (firstLine) return firstLine.length > 60 ? firstLine.slice(0, 60) + '…' : firstLine;
  }
  return 'Untitled prompt';
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}


export function TopPromptsRanked({
  orgId,
  maxHeight,
  compactHeader,
  limit = 10,
  title = 'Top Prompts',
  description = 'Highest-scoring prompts in your organization',
  promptTypeFilter,
  tagFilter,
  showLibraryCta,
}: TopPromptsRankedProps) {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<TopPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<TopPrompt | null>(null);
  const [promptDetail, setPromptDetail] = useState<PromptDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchTopPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = tagFilter
        ? await supabase.rpc('get_org_prompt_feed', {
            _org_id: orgId,
            _limit: Math.max(limit * 4, 40),
            _before_timestamp: null,
          })
        : await supabase.rpc('get_org_top_prompts', {
            _org_id: orgId,
            _limit: Math.max(limit, 10),
          });

      if (error) {
        console.error('Error fetching top prompts:', error);
      } else {
        setPrompts(((data || []) as PromptRpcRow[]).map((row) => ({
          score_id: row.score_id,
          score_total: row.score_total,
          detected_type: row.detected_type || 'Unknown',
          best_prompt: row.best_prompt,
          user_id: row.user_id,
          user_name: row.user_name,
          created_at: row.created_at,
          evaluation_markdown: row.evaluation_markdown,
          tags: row.tags || [],
        })));
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, limit, tagFilter]);

  useEffect(() => {
    fetchTopPrompts();
  }, [fetchTopPrompts]);

  const viewPromptDetail = async (prompt: TopPrompt) => {
    setSelectedPrompt(prompt);
    setDetailLoading(true);
    setPromptDetail(null);

    try {
      // Use the new unified RPC
      const { data, error } = await supabase.rpc('get_prompt_detail_for_org', {
        _score_id: prompt.score_id,
        _org_id: orgId,
      });

      if (error) {
        console.error('Error fetching prompt detail:', error);
        return;
      }

      if (data && data.length > 0) {
        const detail = data[0];
        setPromptDetail({
          score_id: detail.score_id,
          overall_score: detail.overall_score,
          detected_type: detail.detected_type,
          best_prompt_extracted: detail.best_prompt_extracted,
          dimension_scores_json: detail.dimension_scores_json as Record<string, unknown>,
          evaluation_markdown: detail.evaluation_markdown,
          chatcraft_subscores_json: detail.chatcraft_subscores_json as Record<string, number>,
          original_content: detail.original_content,
          submission_type: detail.submission_type,
          points_total: detail.points_total,
          points_base: detail.points_base,
          points_rubric: detail.points_rubric,
          points_bonus: detail.points_bonus,
          user_name: detail.user_name,
          created_at: detail.created_at,
        });
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedPrompt(null);
    setPromptDetail(null);
  };

  const displayedPrompts = useMemo(() => (
    prompts
      .filter((prompt) => !promptTypeFilter || prompt.detected_type === promptTypeFilter)
      .filter((prompt) => !tagFilter || (prompt.tags || []).includes(tagFilter))
      .slice(0, showAll ? prompts.length : limit)
  ), [prompts, promptTypeFilter, tagFilter, showAll, limit]);

  const activeFilter = promptTypeFilter || tagFilter;

  if (loading) {
    return (
      <Card className="bg-card/80 backdrop-blur-sm border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60 mt-1" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (prompts.length === 0) {
    return (
      <Card className="bg-card/80 backdrop-blur-sm border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No scored prompts yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`bg-card/80 backdrop-blur-sm border-border/50 overflow-hidden flex flex-col ${maxHeight || 'h-full'}`}>
        <CardHeader className="sticky top-0 bg-card/95 backdrop-blur-sm z-10 border-b border-border/30 pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className={`flex items-center gap-2 ${compactHeader ? '' : 'text-base'}`}>
                <Trophy className="h-5 w-5 text-primary" />
                {title}
              </CardTitle>
              <CardDescription>
                {activeFilter ? `${description} filtered by ${activeFilter}` : description}
              </CardDescription>
            </div>
            {showLibraryCta && (
              <Button variant="outline" size="sm" onClick={() => navigate('/exec-dashboard/library')}>
                View Library
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-0 flex-1 overflow-y-auto p-2">
          {displayedPrompts.length > 0 ? displayedPrompts.map((prompt, index) => (
            <div key={prompt.score_id}>
              <button
                onClick={() => viewPromptDetail(prompt)}
                className="w-full text-left flex flex-col gap-1 py-2.5 hover:bg-muted/30 transition-colors cursor-pointer rounded-lg px-1"
              >
                {/* Row 1: Timestamp pinned right */}
                <div className="flex justify-end">
                  <span className="text-xs text-muted-foreground">
                    {formatRelativeTime(prompt.created_at)}
                  </span>
                </div>

                {/* Row 2: Rank + Score XX/100 + "Prompt Score" + Category badge */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <span className="text-xs font-bold text-yellow-500">{index + 1}</span>
                    </div>
                    <span className={`font-bold text-lg ${getScoreColor(prompt.score_total)}`}>
                      {prompt.score_total}
                    </span>
                    <span className="text-sm text-muted-foreground/60">/100</span>
                    <span className="text-xs text-muted-foreground ml-0.5">Prompt Score</span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`text-xs border flex-shrink-0 rounded-md ${CATEGORY_COLORS[prompt.detected_type] || CATEGORY_COLORS['Unknown']}`}
                  >
                    {prompt.detected_type}
                  </Badge>
                </div>

                {/* Row 3: Title (bold) + Star */}
                <div className="flex items-center justify-between gap-2 pl-4">
                  <span className="text-[15px] font-semibold text-foreground line-clamp-2">
                    {getPromptTitle(prompt)}
                  </span>
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton scoreId={prompt.score_id} size="sm" />
                  </div>
                </div>
                {prompt.tags && prompt.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 pl-4">
                    {prompt.tags.slice(0, 4).map((tag) => (
                      <Badge key={tag} variant="secondary" className="rounded-md text-[11px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </button>
              
              {/* Separator between entries */}
              {index < displayedPrompts.length - 1 && (
                <Separator className="opacity-30" />
              )}
            </div>
          )) : (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No prompts match this filter yet.
            </div>
          )}

          {!showAll && prompts.length > limit && displayedPrompts.length >= limit && (
            <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setShowAll(true)}>
              Show more prompts
            </Button>
          )}

        </CardContent>
      </Card>

      {/* Detail Modal - using shared component */}
      <PromptDetailModal
        isOpen={!!selectedPrompt}
        onClose={handleCloseModal}
        data={promptDetail}
        loading={detailLoading}
        showUserName={false}
      />
    </>
  );
}
